
import { Component, Watch, Vue } from "vue-property-decorator";
import { FilterBarItems, TableMenuAction, TrainingElementVersions } from "@/models";
import OsdiTooltipButton from "shared-alva/components/buttons/OsdiTooltipButton.vue";
import { MachineTypes, Owner, Training } from "shared-alva/models";
import { getModule } from "vuex-module-decorators";
import OsdiTable, { OsdiTableRowAction } from "shared-alva/components/tables/OsdiTable.vue";
import DialogStore from "@/store/modules/dialog-store";
import TopFiltersBar from "@/components/TopFiltersBar.vue";
import OsdiConfirmDialog from "shared-alva/components/dialogs/OsdiConfirmDialog.vue";
import { hasPermission } from "@/authentication/authentication.service";
import TrainingsModule from "@/store/modules/trainings-store_v2";
import { availableLanguages, languages } from "shared-alva/languages";
import { MachineTypesAPI } from "shared-alva";
import UIStateModule from "@/store/modules/uistate-store";
import awsconfig from "@/aws-config";
import Changelog from "@/components/Changelog.vue";
import { contentFilterItems, refilterContent, sortItems } from "@/service/contentHandlers";
import LanguageModule from "@/store/modules/language-store";
import i18n from "@/i18n";
import { sort } from "semver";
import { DataTableHeader } from "vuetify";
import { ROUTES } from "@/router";
import FiltersModule from "@/store/modules/filters-store";
import { filterBarItems } from "@/utils/filterBarItems";
import { tableHeaders } from "@/utils/tableHeaders";
import OsdiTableItem from "./TableItem/TrainingTableItem.vue";
import OsdiDraftTableItem from "@/components/tableTools/TableItemDraft.vue";
import OsdiTableFooter from "@/components/tableTools/TableFooter.vue";
import { v4 as uuid } from "uuid";
import { VProgressLinear, VSelect } from "vuetify/lib";

const dialogStore = getModule(DialogStore);

@Component({
  components: {
    OsdiConfirmDialog,
    OsdiTable,
    TopFiltersBar,
    OsdiTooltipButton,
    Changelog,
    OsdiTableItem,
    OsdiDraftTableItem,
    OsdiTableFooter,
  },
})
export default class TrainingOverview extends Vue {
  $refs!: {
    confirmDeleteDialog: OsdiConfirmDialog;
  };

  private machineTypesAPI = new MachineTypesAPI(awsconfig.API_V2);
  actions: OsdiTableRowAction[] = [];
  private uiStore = UIStateModule;
  private elementPath = "training";
  trainingStore = TrainingsModule;
  filteredItems: TrainingElementVersions[] = [];
  expandedItems: TrainingElementVersions[] = [];

  private filteredNewVersion: any = "";
  selectedVersion: { [id: string]: string } = {};

  showChangelog: boolean = false;
  changelogData?: Training;

  private machineTypes: string[] = [];
  private searchInput: string = "";
  filterStore = getModule(FiltersModule);
  private needExtended: boolean = false;
  tableMenu: TableMenuAction[] = [
    {
      label: "createNewTranslation",
      icon: "mdi-translate",
      action: (training: Training) => this.newVersionDialog(training, true),
    },
    {
      label: "createNewVersion",
      icon: "mdi-source-branch-plus",
      action: (training: Training) => this.newVersionDialog(training, false),
    },
    {
      label: "showChangelog",
      icon: "mdi-history",
      action: (training: Training) => {
        this.openChangelog(training);
      },
    },
  ];
  languageStore = getModule(LanguageModule);
  filterBarItems = this.getFilterBarItems;

  @Watch("languageStore.language")
  refreshFilters() {
    this.filterBarItems = this.getFilterBarItems;
  }

  get getFilterBarItems(): FilterBarItems[] {
    return [
      {
        label: i18n.t("smartcollege.overviewTable.headers.status").toString(),
        items: [
          i18n.t("smartcollege.filters.all").toString(),
          i18n.t("smartcollege.filters.draft").toString(),
          i18n.t("smartcollege.filters.review").toString(),
          i18n.t("smartcollege.filters.approved").toString(),
          i18n.t("smartcollege.filters.published").toString(),
        ],
        value: i18n.t("smartcollege.filters.all").toString(),
        onChange: () => {},
      },
      {
        label: i18n.t("smartcollege.overviewTable.headers.language").toString(),
        items: ["Default", ...availableLanguages],
        itemLabels: ["Default", ...availableLanguages].flatMap((el: string) => {
          return i18n.t(`smartcollege.language.${el}`).toString();
        }),
        value: i18n.locale,
        onChange: () => {},
      },
      {
        label: i18n.t("smartcollege.overviewTable.headers.targetGroups").toString(),
        items: [
          i18n.t("smartcollege.filters.all").toString(),
          i18n.t("smartcollege.targetGroups.MECHANIC").toString(),
          i18n.t("smartcollege.targetGroups.ELECTRICIAN").toString(),
          i18n.t("smartcollege.targetGroups.OPERATOR").toString(),
        ],
        value: i18n.t("smartcollege.filters.all").toString(),
        onChange: () => {},
      },

      {
        label: i18n.t("smartcollege.overviewTable.headers.assetTypes").toString(),
        items: [i18n.t("smartcollege.filters.all").toString(), ...this.machineTypes],
        value: i18n.t("smartcollege.filters.all").toString(),
        onChange: () => {},
      },
    ];
  }

  get getStatusMap(): { [id: string]: string } {
    return {
      [i18n.t("smartcollege.filters.all").toString()]: "All",
      [i18n.t("smartcollege.filters.review").toString()]: "Review",
      [i18n.t("smartcollege.filters.approved").toString()]: "Approved",
      [i18n.t("smartcollege.filters.published").toString()]: "Published",
      [i18n.t("smartcollege.filters.draft").toString()]: "Draft",
    };
  }

  get getTypeMap(): { [id: string]: string } {
    return {
      [i18n.t("smartcollege.filters.all").toString()]: "All",
      [i18n.t("smartcollege.targetGroups.MECHANIC").toString()]: "MECHANIC",
      [i18n.t("smartcollege.targetGroups.ELECTRICIAN").toString()]: "ELECTRICIAN",
      [i18n.t("smartcollege.targetGroups.OPERATOR").toString()]: "OPERATOR",
    };
  }

  get tableHeaders(): DataTableHeader[] {
    return [
      {
        text: "",
        value: "id",
        width: "1%",
        sortable: false,
      },
      {
        text: i18n.t("smartcollege.overviewTable.headers.title").toString(),
        sortable: true,
        value: "title",
      },
      {
        text: i18n.t("smartcollege.overviewTable.headers.version").toString(),
        align: "start",
        sortable: false,
        value: "version",
        width: "2%",
      },
      {
        text: i18n.t("smartcollege.overviewTable.headers.status").toString(),
        value: "status",
        sortable: false,
      },
      {
        text: i18n.t("smartcollege.overviewTable.headers.language").toString(),
        value: "language",
        sortable: false,
      },
      {
        text: i18n.t("smartcollege.overviewTable.headers.targetGroups").toString(),
        value: "targetGroups",
        sortable: false,
      },
      {
        text: i18n.t("smartcollege.overviewTable.headers.assetTypes").toString(),
        value: "assetTypes",
        sortable: false,
      },
      {
        text: "",
        value: "context-menu",
        width: "1%",
        sortable: false,
      },
      {
        text: "",
        value: "data-table-expand",
        sortable: false,
      },
    ];
  }

  async created() {
    if (await hasPermission("deleteTraining")) {
      this.actions.push({
        tooltip: this.$t("smartcollege.trainingeditor.actions.deleteTraining").toString(),
        icon: "mdi-delete",
        callback: async (training: Training) => await this.deleteTraining(training),
      });
    }
  }

  @Watch("filterBarItems", { deep: true })
  @Watch("searchInput")
  changeSearchInput() {
    this.filterStore.setAllFilters({
      element: this.elementPath,
      filters: this.filterBarItems,
      searchInput: this.searchInput,
    });
  }
  async mounted() {
    const owner: Owner = {
      tenant: "fettecompacting",
      siteId: "global",
    };
    const { types }: MachineTypes = (await this.machineTypesAPI.getMachineTypes(
      owner
    )) as MachineTypes;

    this.machineTypes = types;

    this.filterBarItems[3].items = [
      i18n.t("smartcollege.filters.all").toString(),
      ...this.machineTypes,
    ];

    if (this.filterStore.filters[this.elementPath]) {
      this.filterBarItems = this.filterStore.filters[this.elementPath].filterItems;
      this.searchInput = this.filterStore.filters[this.elementPath].searchInput;
      this.uiStore.setSearchInput(this.searchInput);
    } else {
      this.filterStore.setAllFilters({
        element: this.elementPath,
        filters: this.filterBarItems,
        searchInput: this.searchInput,
      });
    }
  }

  newVersionDialog(training: Training, translate: boolean) {
    dialogStore.openDialog({
      componentName: "new-version-dialog",
      data: {
        baseElement: { ...training, type: "training" },
        translate,
      },
      customDialog: true,
    });
  }

  sort(items: any, index: string, isDescending: string) {
    const sortedItems = sortItems(items, index, isDescending, true);
    return sortedItems;
  }

  get selectedLanguage(): languages {
    if (this.filterBarItems[1].value == "Default") return this.$i18n.locale as languages;
    return this.filterBarItems[1].value as languages;
  }

  get selectedMachineType(): string {
    let filtersMap = {
      [i18n.t("smartcollege.filters.all").toString()]: "All",
    };

    const machinesMaps: { [id: string]: string } = {};

    this.filterBarItems[3].items.forEach((elem) => {
      machinesMaps[elem] = elem;
    });

    filtersMap = { ...machinesMaps, ...filtersMap };

    return filtersMap[this.filterBarItems[3].value];
  }

  get selectedTargetGroup(): string {
    return this.getTypeMap[this.filterBarItems[2].value];
  }
  get selectedStatus(): string {
    return this.getStatusMap[this.filterBarItems[0].value];
  }

  getSelectedVersion(id: string): string {
    return this.selectedVersion[id] ?? "";
  }

  openChangelog(training: Training) {
    this.changelogData = training;
    this.showChangelog = true;
  }

  changeVersion(id: string, e: string): any {
    this.selectedVersion[id] = e;

    //finds the correct object that we want to display
    const itemFind = this.filteredItems.find((f) => f.id == id);
    const versionFind = itemFind?.publishedVersions[e];
    this.filteredNewVersion = versionFind;

    this.reloadFilteredItems();
  }

  closeChangelog(): void {
    this.showChangelog = !this.showChangelog;
  }

  @Watch("selectedMachineType")
  @Watch("selectedTargetGroup")
  @Watch("selectedStatus")
  @Watch("searchInput.length")
  @Watch("trainingStore.trainingList", { immediate: true })
  async reloadFilteredItems() {
    const trainings = [...(await this.trainingStore.getAllTrainingVersions(this.selectedLanguage))];

    const filteredValues = contentFilterItems(
      trainings,
      this.filteredItems,
      this.expandedItems,
      this.selectedVersion,
      this.selectedTargetGroup,
      this.selectedStatus,
      this.selectedMachineType,
      this.filteredNewVersion,
      this.searchInput,
      true
    );

    this.filteredItems = filteredValues.filteredItems;
    this.expandedItems = filteredValues.expandedItems;
  }

  @Watch("selectedLanguage")
  async languageChanged() {
    //request sublessons with the new language and then refilter them with the searchInput value
    const content = [...(await this.trainingStore.getAllTrainingVersions(this.selectedLanguage))];
    const refilteredContent = refilterContent(
      content,
      this.searchInput,
      this.selectedStatus,
      this.selectedVersion,
      true
    );
    this.filteredItems = refilteredContent.filteredItems;
    this.expandedItems = refilteredContent.expandedItems;
  }

  createNewTraining() {
    // openCreateContentDialog("training");
    this.$router.push({
      name: ROUTES.SmartCollegesTrainingNew,
    });
  }

  async deleteTraining(training: Training) {
    this.$refs.confirmDeleteDialog.open({
      confirmCallback: async () => {
        this.trainingStore.deleteTraining(training);
      },
      message: this.$t("smartcollege.list.deleteNotification.message", {
        name: training.title,
      }).toString(),
    });
  }

  goToTraining(trainingId: string, version: string, language: string) {
    this.$router.push({
      name: ROUTES.SmartCollegeTrainingDetail,
      params: {
        trainingId: trainingId,
      },
      query: {
        version: version,
        language: language,
      },
    });
  }

  @Watch("uiStore.getSearchInput")
  searchInputs() {
    return (this.searchInput = this.uiStore.getSearchInput);
  }

  genId() {
    return uuid();
  }
}
